import Papa from 'papaparse';
import type { TradeItem } from '@/types';
import { buildCsvTotalsRows, SYNTHETIC_CSV_ACCESSORS } from './csv-totals';
import { csvFilenameStamp } from './csv-filename';
import { buildMtmSyntheticCcyBuckets, mtmSyntheticRowCells } from './mtm-synthetic-totals';
import type { RepoCallScope } from './mtm-cell-helpers';
import { MTM_SYNTHETIC_NEW_MARGIN } from '@/stores/trades/mtm-column-store';
import { recordExportEvent } from '@/services/audit/audit-service';

type ColumnConfig = {
  id: string;
  label: string;
  visible: boolean;
};

/** Column header for the computed new-mark margined value appended to the export. */
const NEW_MARGIN_LABEL = 'New Margin Value';

const generateFilename = (): string => `mtm-marks-${csvFilenameStamp()}.csv`;

/**
 * Export the Mark-to-Market book to CSV (LR-469). Mirrors exportTradesToCSV but:
 *  - receives the active bucket's selection scope — stock L/B markable rows AND (LR-548) selectable
 *    repo/reverse rows — the same set the on-page totals footer reads, so the screen totals and the
 *    exported file reconcile (kvi #1);
 *  - drops rows the user unchecked in their bucket: stock via excludedIds, repo via repoExcludedIds
 *    (LR-548), snapshotting both sets at call time;
 *  - fills the three override-dependent synthetic columns PER ROW (New Margin Value /
 *    Gross Value Δ / Margin Value Δ) from `mtmSyntheticRowCells` — they are not TradeItem fields,
 *    so without this they exported blank; New Margin Value is additionally appended even when its
 *    column is hidden, so the file always carries the margin values about to be submitted;
 *  - appends per-currency subtotals + a EUR/USD-equivalent grand total. Real monetary columns
 *    total via SUMMABLE_COLUMNS; the three synthetic ones are pre-aggregated from the SAME helper
 *    that produced their row cells, so the file reconciles both with itself and with the screen.
 *
 * `invalidIds` mirrors MtmTable's red-bordered New Price inputs: those cells render "—", so the
 * rows behind them contribute neither a row value nor a total. Omit it and an invalid row exports
 * its default-price delta while showing "—" on screen.
 */
export const exportMtmToCSV = (
  rows: TradeItem[],
  priceOverrides: Map<number, string>,
  excludedIds: Set<number>,
  visibleColumns: ColumnConfig[],
  baseCurrency = 'EUR',
  repoExcludedIds: Set<number> = new Set(),
  repoCall?: RepoCallScope,
  invalidIds?: Set<number>,
): void => {
  // Snapshot both deselection sets so a late store mutation can't change what we emit. Each row is
  // kept unless it's unchecked in ITS bucket (stock → excludedIds, repo/reverse → repoExcludedIds).
  const excluded = new Set(excludedIds);
  const repoExcluded = new Set(repoExcludedIds);
  const data = rows.filter((r) => {
    const isRepo = r.trade_type === 'repo' || r.trade_type === 'reverse_repo';
    return isRepo ? !repoExcluded.has(r.id) : !excluded.has(r.id);
  });
  if (data.length === 0) {
    // Distinguish the two ways of getting here: everything unchecked (the trader's own doing)
    // vs nothing in this view being markable/callable at all — which is what happens when the
    // counterparty has no GMRA agreement, and blaming unchecked rows there sends the trader
    // looking at checkboxes instead of at the counterparty's agreements.
    alert(
      rows.length === 0
        ? 'Nothing in this view is part of the MTM run or the repo margin call, so there is nothing to export.'
        : 'No selected rows to export in the current bucket. Unchecked rows are excluded.',
    );
    return;
  }

  const filename = generateFilename();

  const visibleColumnIds = visibleColumns
    .filter((col) => col.visible && col.id !== 'rowNumber')
    .map((col) => col.id);

  const columnLabels = visibleColumns
    .filter((col) => visibleColumnIds.includes(col.id))
    .reduce(
      (acc, col) => {
        acc[col.id] = col.label;
        return acc;
      },
      {} as Record<string, string>,
    );

  const csvData = data.map((row) => {
    const csvRow: Record<string, unknown> = {};
    // Computed at the submit price (override ?? cob ?? gross); blank for a row rendering "—".
    const synthetic = mtmSyntheticRowCells(row, priceOverrides.get(row.id), repoCall, invalidIds);
    visibleColumnIds.forEach((colId) => {
      const value =
        colId in synthetic
          ? synthetic[colId]
          : colId in SYNTHETIC_CSV_ACCESSORS
            ? SYNTHETIC_CSV_ACCESSORS[colId](row)
            : row[colId as keyof TradeItem];
      csvRow[columnLabels[colId]] = value ?? '';
    });
    csvRow[NEW_MARGIN_LABEL] = synthetic[MTM_SYNTHETIC_NEW_MARGIN];
    return csvRow;
  });

  // Per-currency subtotals + EUR/USD-equivalent grand total over the same rows. The synthetic
  // buckets are keyed by column id, so a hidden synthetic column simply never reaches the block.
  // `byColumn` already applies the mixed-product rule (the two delta columns come back empty
  // when stock L/B and repo are both in the export scope), so the file's totals block matches
  // the screen's footer without the exporter knowing the rule.
  const { byColumn: syntheticBuckets } = buildMtmSyntheticCcyBuckets(
    data,
    priceOverrides,
    repoCall,
    invalidIds,
  );
  // New Margin Value is appended to every row even when its grid column is hidden, so it has to
  // reach the totals block by the same exception — otherwise the file carries a money column with
  // per-row figures and no subtotal, next to columns that have one.
  const totalsColumnIds = visibleColumnIds.includes(MTM_SYNTHETIC_NEW_MARGIN)
    ? visibleColumnIds
    : [...visibleColumnIds, MTM_SYNTHETIC_NEW_MARGIN];
  const totalsLabels = { ...columnLabels, [MTM_SYNTHETIC_NEW_MARGIN]: NEW_MARGIN_LABEL };
  csvData.push(
    ...buildCsvTotalsRows(data, totalsColumnIds, totalsLabels, baseCurrency, syntheticBuckets),
  );

  // escapeFormulae: prefix any cell starting with =,+,-,@ with a quote so a security
  // whose description/symbol/isin is e.g. `=HYPERLINK(...)` (sourced from backend feeds,
  // not validated input) can't execute when the trader opens the CSV in Excel/Sheets.
  const csv = Papa.unparse(csvData, { escapeFormulae: true });

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);

  recordExportEvent('mtm.export_csv', { row_count: data.length });
};
